import * as apiConstants from './apiUtilsConstants';
import { callUIAPI } from './apiUtils';
import { clientLogger } from '../microscope/LoggerBoundary';

export const JOB_CANCELLED = 'JOB_CANCELLED';

const cancelledError = (message) => {
  const error = new Error(message);
  error.code = JOB_CANCELLED;
  return error;
};

export const isJobCancelledError = (error) => error?.code === JOB_CANCELLED;

export const pollJob = (statusEndpoint, onProgress, options = {}) => {
  const {
    intervalMs = 3000,
    timeoutMs = 300000,
    signal,
  } = options;

  const startTime = Date.now();
  let lastLogIndex = 0;

  return new Promise((resolve, reject) => {
    let timer = null;
    let settled = false;

    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      if (signal) signal.removeEventListener('abort', onAbort);
      fn(value);
    };

    function onAbort() {
      finish(reject, cancelledError('Operation was cancelled.'));
    }

    if (signal) {
      if (signal.aborted) {
        reject(cancelledError('Operation was cancelled.'));
        return;
      }
      signal.addEventListener('abort', onAbort);
    }

    const scheduleNext = () => {
      if (settled) return;
      timer = setTimeout(checkStatus, intervalMs);
    };

    const checkStatus = () => {
      if (settled) return;
      if (Date.now() - startTime > timeoutMs) {
        finish(reject, new Error('Operation timed out. Please try again.'));
        return;
      }

      callUIAPI(statusEndpoint, null, apiConstants.METHOD_GET, true)
        .then(response => {
          const { status, result, data, error, errorCode, progressLogs } = response.data;
          const finalResult = result !== undefined ? result : data;

          if (progressLogs && Array.isArray(progressLogs)) {
            const newLogs = progressLogs.slice(lastLogIndex);
            if (newLogs.length > 0) {
              newLogs.forEach(log => {
                if (onProgress) onProgress(log);
              });
              lastLogIndex = progressLogs.length;
            }
          }

          if (status === 'COMPLETED') {
            finish(resolve, { data: finalResult });
          } else if (status === 'FAILED') {
            const failure = new Error(error || 'Operation failed. Please try again.');
            if (errorCode) failure.errorCode = errorCode;
            finish(reject, failure);
          } else if (status === 'CANCELLED') {
            finish(reject, cancelledError('Operation was cancelled.'));
          } else {
            scheduleNext();
          }
        })
        .catch(err => {
          if (settled) return;
          if (err.response && err.response.status === 404) {
            finish(reject, cancelledError('Job not found — it may have been cancelled.'));
          } else {
            clientLogger.warn(`Job polling error: ${err?.message || 'Unknown error'}`, {
              statusEndpoint,
              status: err?.response?.status,
              error: err?.message,
            });
            scheduleNext();
          }
        });
    };

    checkStatus();
  });
};

export const startAndPollJob = async (startEndpoint, payload, statusBase, onProgress, options = {}) => {
  const { onJobCreated, ...pollOptions } = options;
  const response = await callUIAPI(startEndpoint, payload, apiConstants.METHOD_POST, true);
  const { jobId } = response.data;

  if (typeof onJobCreated === 'function') {
    onJobCreated(jobId);
  }

  const statusEndpoint = `${statusBase}/${jobId}`;
  return pollJob(statusEndpoint, onProgress, pollOptions);
};